import { Button, makeStyles, Text, tokens, Title2 } from '@fluentui/react-components';
import { ErrorCircle24Regular } from '@fluentui/react-icons';

import { useI18n } from '../lib/i18n/hooks';

const useStyles = makeStyles({
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    maxWidth: '480px',
    textAlign: 'center',
  },
  icon: {
    color: tokens.colorStatusDangerForeground1,
    fontSize: '48px',
  },
  message: {
    color: tokens.colorNeutralForeground2,
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
});

interface ErrorFallbackProps {
  resetError?: () => void;
}

export function ErrorFallback({ resetError }: ErrorFallbackProps = {}): React.ReactElement {
  const styles = useStyles();
  const { t } = useI18n();

  return (
    <div className={styles.container} role="alert">
      <div className={styles.content}>
        <ErrorCircle24Regular className={styles.icon} aria-hidden="true" />
        <Title2>{t('common.something-went-wrong', { defaultValue: 'Something went wrong' })}</Title2>
        <Text className={styles.message}>
          {t('common.unexpected-error', {
            defaultValue: 'An unexpected error occurred. Please try reloading the page.',
          })}
        </Text>
        <div className={styles.actions}>
          {resetError && (
            <Button appearance="primary" onClick={resetError}>
              {t('common.try-again', { defaultValue: 'Try again' })}
            </Button>
          )}
          <Button appearance={resetError ? 'secondary' : 'primary'} onClick={() => window.location.reload()}>
            {t('common.reload-page', { defaultValue: 'Reload page' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
